import React, { useState, Fragment, useRef } from "react";
import styled from "@emotion/styled";
import { useMutation } from "@apollo/client";
import { Link, useRouteMatch } from "react-router-dom";
import { IoIosArrowDown, IoMdMore, IoIosFlag } from "react-icons/io";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,

  Chip,

  Paper,
  Divider,
} from "@mui/material";
import { ProfilePicture } from "../../../components/common/profile-picture";
import { UNBECOME_TEACHER } from "../../../apollo-client/mutations";
import {
  GET_COURSE_TEACHINGS,
  GET_USER_DATA,
} from "../../../apollo-client/queries";
import chat_icon from "../../../img_new/Chat_Icon.svg";
import accepts_remote_icon from "../../../img/remote.svg";
import { TeacherDescription } from "./teacherDescription";
import { AvgResponseTimeComponent } from "../../teachers-page/avg-response-time-component";
import { EditTeachingDialog } from "../../teachers-page/dialogs/edit-teaching-dialog";
import { useIsMobile } from "../../../hooks/state-checkers/use-is-mobile";
import { TeacherSettings } from "../teacher-detail-comments/TeacherSettings";
import { useReportStore } from "../../../store/reportStore";
import { useOutsideMenuCloserWithFunction } from "../../../helpers";
import { FormattedMessage } from "react-intl";
import { CONTENT_TYPES } from "../../../constants";

export const TeacherDetailProfile = ({
  courseId,
  courseTitle,
  handleStartTeacherChat,
  additionalTeacherTeachings,
  props,
  teaching,
}) => {
  const { _id, price, description, isTeacher } = teaching;

  const { profilePicture, username } = teaching.teacher;

  const [isEditTeacherPopupOpen, setIsEditTeacherPopupOpen] = useState(false);

  const isMobile = useIsMobile();

  const { acceptsRemote } = teaching;

  const [unbecomeTeacher] = useMutation(UNBECOME_TEACHER, {
    variables: { course: courseId },
    refetchQueries: [GET_COURSE_TEACHINGS, "teachings", GET_USER_DATA, "user"],
  });

  const [showBox, setShowBox] = useState(false);
  const [showReportMenu, setShowReportMenu] = useState(false);
  const { reportState, setReportState, openReport } = useReportStore();

  const wrapperRef = useRef(null);
  const reportMenuRef = useRef(null);
  useOutsideMenuCloserWithFunction(wrapperRef, () => setShowBox(false));
  useOutsideMenuCloserWithFunction(reportMenuRef, () => setShowReportMenu(false));

  const deactivateTeacher = async () => {
    await unbecomeTeacher();
    props.history.push(`/courses/${props.match.params.id}/teachers`);
  };


  return (
    <Fragment>
      <Card
        sx={{
          borderRadius: { xs: 0, md: 3 },
          boxShadow: { xs: 'none', md: '0 4px 20px rgba(0, 0, 0, 0.1)' },
          overflow: 'hidden',
          mb: 2,
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 2, md: 3 },
              alignItems: { xs: 'flex-start', md: 'flex-start' },
            }}
          >
            {/* Profile Picture and Price Section */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'row', md: 'column' },
                alignItems: { xs: 'center', md: 'flex-start' },
                gap: 2,
                minWidth: { xs: 'auto', md: '120px' },
              }}
            >
              <ProfilePicture
                src={profilePicture || undefined}
                width={isMobile ? 60 : 100}
                height={isMobile ? 60 : 100}
              />
              {!isMobile && (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: 'primary.main',
                      fontSize: { xs: '1.5rem', md: '2rem' },
                    }}
                  >
                    {price} €/h
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Main Content Section */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Header with Name and Actions */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 2,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '1.25rem', md: '1.75rem' },
                      mb: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {username}
                  </Typography>
                  
                  {/* Mobile Price Display */}
                  {isMobile && (
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: 'primary.main',
                        mb: 1,
                      }}
                    >
                      {price} €/h
                    </Typography>
                  )}

                  {/* Remote Teaching Indicator */}
                  {acceptsRemote && (
                    <Chip
                      icon={
                        <img
                          src={accepts_remote_icon}
                          alt="Remote"
                          style={{ width: 16, height: 16 }}
                        />
                      }
                      label={<FormattedMessage id="teacher.general.accept-remote-label" />}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mb: 1 }}
                    />
                  )}
                </Box>

                {/* Actions Section */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    ml: 2,
                  }}
                >
                  
                  <AvgResponseTimeComponent
                    avgResponseTime={teaching.teacher.avgResponseTime}
                  />
                  
                  {!isTeacher ? (
                    <>
                      <IconButton
                        onClick={handleStartTeacherChat}
                        sx={{
                          p: 1.5,
                          '&:hover': {
                            backgroundColor: 'primary.50',
                            transform: 'scale(1.05)',
                          },
                          transition: 'all 0.2s ease-in-out',
                        }}
                      >
                        <img
                          src={chat_icon}
                          alt="Chat"
                          style={{ width: 40, height: 40 }}
                        />
                      </IconButton>
                      
                      {/* Subtle three-dot menu for report option */}
                      <Box sx={{ position: 'relative' }} ref={reportMenuRef}>
                        <IconButton
                          onClick={() => setShowReportMenu(!showReportMenu)}
                          sx={{
                            p: 1,
                            color: 'text.secondary',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            },
                          }}
                        >
                          <IoMdMore size={20} />
                        </IconButton>
                        
                        {showReportMenu && (
                          <Paper
                            sx={{
                              position: 'absolute',
                              top: '100%',
                              right: 0,
                              mt: 1,
                              minWidth: 120,
                              boxShadow: 2,
                              borderRadius: 1,
                              zIndex: 1000,
                            }}
                          >
                            <Box
                              onClick={() => {
                                openReport(_id, CONTENT_TYPES.TEACHING);
                                setShowReportMenu(false);
                              }}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 2,
                                py: 1.5,
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: 'action.hover',
                                },
                              }}
                            >
                              <IoIosFlag size={16} color="#f44336" />
                              <Typography variant="body2" color="text.primary">
                                <FormattedMessage id="general.report" />
                              </Typography>
                            </Box>
                          </Paper>
                        )}
                      </Box>
                    </>
                  ) : (
                    <TeacherSettings
                      isContributor={true}
                      deleteMutation={deactivateTeacher}
                      toggleEdit={() =>
                        setIsEditTeacherPopupOpen(!isEditTeacherPopupOpen)
                      }
                      mirrorSite={true}
                    />
                  )}
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Teacher Description */}
              <TeacherDescription
                description={description}
                additionalTeacherTeachings={additionalTeacherTeachings}
                currentCourseTitle={courseTitle}
                teaching={teaching}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>
      
      <EditTeachingDialog
        teaching={teaching}
        isDialogOpen={isEditTeacherPopupOpen}
        closeDialog={() => setIsEditTeacherPopupOpen(!isEditTeacherPopupOpen)}
      />
    </Fragment>
  );
};

// Modernized component - old styled components removed
